import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ActionsGroup,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  FlatButton,
  IconButton,
  WaitingButton,
} from "@evergis/ui";

import { ModalDialog } from "../DialogComponents/ModalDialog";
import { Content, Description, LinearProgress, LoadingDescription } from "./styled";

import { isEditable } from "../../../evergis/core/permissions";
import { useFeature } from "../../../hooks/feature/useFeature";
import { useProjectLayerInfos } from "../../../hooks/project/useProjectLayers";
import { useFeatureToShow } from "../../../modules/FeatureSelector/hooks/useFeatureToShow";
import { useDialogReset } from "../../../hooks/useDialogs";
import { useCopyFeatureJob } from "./hooks/useCopyFeatureJob";

export const CopyFeature: FC = () => {
  const { t } = useTranslation();
  const { feature: featureToCopy } = useFeatureToShow();
  const { geometryType: featureGeometryType, featureName, featureLayer } = useFeature(featureToCopy);
  const { layerInfos, loading } = useProjectLayerInfos();
  const closeDialog = useDialogReset();
  const [layerToCopy, onChangeLayerToCopy] = useState<string>("");
  const { loading: jobLoading, onCopy } = useCopyFeatureJob(layerToCopy, featureToCopy);
  const copyToLayers = layerInfos.filter(item => {
    return item.geometryType === featureGeometryType && isEditable(item.permissions);
  });
  const layersDropdownOptions = copyToLayers.map(item => ({ value: item.name, text: item.alias }));
  const isFeatureLayerEditable = !!copyToLayers.find(item => item.name === featureLayer);

  useEffect(() => {
    if (isFeatureLayerEditable) {
      onChangeLayerToCopy(featureLayer);
    }
  }, [isFeatureLayerEditable, featureLayer]);

  return (
    <ModalDialog maxWidth={"40rem"}>
      <DialogTitle>
        <ActionsGroup>{t("copyFeature.copyFeature", { ns: "dialogs" })}</ActionsGroup>
        <ActionsGroup>
          <IconButton kind="close" onClick={closeDialog} />
        </ActionsGroup>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Content>
            <LoadingDescription>{t("copyFeature.gettingData", { ns: "dialogs" })}</LoadingDescription>
            <LinearProgress />
          </Content>
        ) : (
          <Content>
            <Description>
              {t("copyFeature.feature", { ns: "dialogs" })} {featureName}{" "}
              {t("copyFeature.willBeCopiedWithSameAttributes", { ns: "dialogs" })}
            </Description>
            <Dropdown
              label={`${t("copyFeature.copyToLayer", { ns: "dialogs" })}:`}
              width={"33rem"}
              zIndex={120}
              placeholder={t("copyFeature.notChosen", { ns: "dialogs" })}
              value={layerToCopy}
              options={layersDropdownOptions}
              onChange={e => e && e[0] && onChangeLayerToCopy(e[0].value)}
            />
          </Content>
        )}
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <WaitingButton primary onClick={onCopy} disabled={loading || !layerToCopy} isWaiting={jobLoading}>
            {t("actions.copy", { ns: "common" })}
          </WaitingButton>
          <FlatButton onClick={closeDialog}>{t("actions.cancel", { ns: "common" })}</FlatButton>
        </ActionsGroup>
      </DialogActions>
    </ModalDialog>
  );
};
